/**
 * accuracy.ts — Section 3: "Accuracy — And Why It Lies".
 *
 * Two overlapping score histograms (negatives blue, positives green) and a
 * threshold slider (vertical rose line). Everything to the right of the
 * threshold is predicted positive; everything to the left, negative. The 2×2
 * confusion matrix updates live: cells TP / TN / FP / FN colour and count as
 * the threshold sweeps.
 *
 * The status line flags the failure mode the slider is built to expose: when
 * one class is rare, you can hit ~99% accuracy by predicting the majority
 * class every time — and the matrix shows it (one giant TN cell, everything
 * else empty). Accuracy alone is not enough; this is the bridge to Section 4.
 */

import { classificationScores, confusion, type ClassSample } from './data';

const SCORE_MIN = 0;
const SCORE_MAX = 1;

// Histogram buckets. 20 buckets across [0, 1] → each is 0.05 wide. Coarse
// enough that a bucket has several samples; fine enough that the overlap zone
// is visible.
const N_BUCKETS = 20;

export function initAccuracy(): void {
  const canvasEl = document.getElementById('accuracyCanvas') as HTMLCanvasElement | null;
  const sliderEl = document.getElementById('accuracyThresholdSlider') as HTMLInputElement | null;
  const thrValueEl = document.getElementById('accuracyThresholdValue');
  const tpCell = document.getElementById('accuracyTP');
  const tnCell = document.getElementById('accuracyTN');
  const fpCell = document.getElementById('accuracyFP');
  const fnCell = document.getElementById('accuracyFN');
  const accValue = document.getElementById('accuracyValue');
  const statusEl = document.getElementById('accuracyStatus');
  if (
    !canvasEl || !sliderEl || !thrValueEl || !tpCell || !tnCell || !fpCell || !fnCell ||
    !accValue || !statusEl
  ) return;

  const canvas = canvasEl;
  const slider = sliderEl;
  const thrValue = thrValueEl;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;
  const PAD = 40;

  const mapX = (s: number): number => PAD + ((s - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * (W - 2 * PAD);

  // Bucket counts per class. Fixed max for the y axis — recomputed on each draw
  // so the histograms stay readable as the threshold moves (they don't change,
  // but redrawing is cheap and keeps the code stateless).
  const samples: ClassSample[] = classificationScores(0);
  const negatives = samples.filter((s) => s.label === 0).map((s) => s.score);
  const positives = samples.filter((s) => s.label === 1).map((s) => s.score);

  function bucketize(scores: number[]): number[] {
    const counts = new Array(N_BUCKETS).fill(0);
    for (const s of scores) {
      const b = Math.min(N_BUCKETS - 1, Math.max(0, Math.floor(s * N_BUCKETS)));
      counts[b]++;
    }
    return counts;
  }

  const negBuckets = bucketize(negatives);
  const posBuckets = bucketize(positives);
  const maxCount = Math.max(...negBuckets, ...posBuckets);

  function drawAxes(): void {
    ctx!.strokeStyle = '#e2e8f0';
    ctx!.lineWidth = 1;
    ctx!.beginPath();
    // x gridlines at each 0.1.
    for (let i = 0; i <= 10; i++) {
      const x = mapX(i / 10);
      ctx!.moveTo(x, PAD);
      ctx!.lineTo(x, H - PAD);
    }
    ctx!.stroke();
  }

  function draw(): void {
    const threshold = parseFloat(slider.value);
    const c = confusion(samples, threshold);
    const total = c.tp + c.tn + c.fp + c.fn;
    const acc = total === 0 ? 0 : (c.tp + c.tn) / total;

    ctx!.clearRect(0, 0, W, H);
    drawAxes();

    const bucketW = (W - 2 * PAD) / N_BUCKETS;
    const mapH = (count: number): number => ((count / maxCount) * (H - 2 * PAD));

    // Negative histogram bars (slate, semi-transparent so overlap shows).
    ctx!.fillStyle = 'rgba(76, 114, 176, 0.55)'; // blue @ 55% alpha
    for (let i = 0; i < N_BUCKETS; i++) {
      const h = mapH(negBuckets[i]);
      const x = PAD + i * bucketW;
      ctx!.fillRect(x, H - PAD - h, bucketW - 1, h);
    }
    // Positive histogram bars (green, same alpha).
    ctx!.fillStyle = 'rgba(85, 168, 104, 0.55)';
    for (let i = 0; i < N_BUCKETS; i++) {
      const h = mapH(posBuckets[i]);
      const x = PAD + i * bucketW;
      ctx!.fillRect(x, H - PAD - h, bucketW - 1, h);
    }

    // Threshold line — solid rose, full canvas height. The reader's lever.
    ctx!.strokeStyle = '#c44e52';
    ctx!.lineWidth = 2.5;
    ctx!.setLineDash([6, 3]);
    ctx!.beginPath();
    ctx!.moveTo(mapX(threshold), PAD);
    ctx!.lineTo(mapX(threshold), H - PAD);
    ctx!.stroke();
    ctx!.setLineDash([]);

    // Numeric readouts.
    thrValue!.textContent = threshold.toFixed(2);
    tpCell!.textContent = String(c.tp);
    tnCell!.textContent = String(c.tn);
    fpCell!.textContent = String(c.fp);
    fnCell!.textContent = String(c.fn);
    accValue!.textContent = `${(acc * 100).toFixed(1)}%`;

    // Status: flag the "accuracy lies" failure mode. The classes here are
    // balanced (60/60) so we can't actually hit the rare-class pathology, but
    // we CAN show the FP/FN trade-off: pushing accuracy up by moving the
    // threshold usually trades FPs for FNs (or vice versa).
    if (c.fp === 0 && c.fn === 0) {
      statusEl!.textContent = 'Perfect separation — every prediction correct. Accuracy = 100%. Enjoy it; real data is never this clean.';
      statusEl!.className = 'text-sm mt-3 font-semibold text-emerald-600';
    } else if (acc > 0.85) {
      statusEl!.textContent = `Accuracy looks great at ${(acc * 100).toFixed(0)}%, but ${c.fp + c.fn} samples are misclassified. Are the errors the ones that matter?`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-amber-600';
    } else {
      statusEl!.textContent = `Accuracy is only ${(acc * 100).toFixed(0)}% — and it hides WHICH errors you're making. See Section 4.`;
      statusEl!.className = 'text-sm mt-3 font-semibold text-rose-600';
    }
  }

  slider.addEventListener('input', draw);
  draw();
}
